import { useState, useEffect } from 'react';

import { Organization } from '../types/organization.type';

type UseOrganizationEditFormResult = {
  isEditing: boolean;
  updatedOrganization: Organization;
  editedFields: Partial<Organization>;
  handleIdentityChange: ({ field, value }: { field: keyof Organization; value: string }) => void;
  handleAddressChange: ({ field, value }: { field: keyof Organization['address']; value: string }) => void;
  handleOfferChange: ({ field, value }: { field: keyof Organization['offer']; value: string | number | boolean }) => void;
  handleCancelEditing: () => void;
  handleSave: (updateFn: (editedFields: Partial<Organization>) => Promise<void>) => Promise<void>;
};

export function useOrganizationEditForm(organization: Organization): UseOrganizationEditFormResult {
  const [isEditing, setIsEditing] = useState(false);
  const [updatedOrganization, setUpdatedOrganization] = useState<Organization>(organization);
  const [editedFields, setEditedFields] = useState<Partial<Organization>>({});

  useEffect(() => {
    setUpdatedOrganization(organization);
    setEditedFields({});
    setIsEditing(false);
  }, [organization]);

  const handleCancelEditing = () => {
    setUpdatedOrganization(organization);
    setEditedFields({});
    setIsEditing(false);
  };

  const handleSave = async (updateFn: (editedFields: Partial<Organization>) => Promise<void>) => {
    if (!editedFields || Object.keys(editedFields).length === 0) return;
    await updateFn(editedFields);
  };

  const handleIdentityChange = ({ field, value }: { field: keyof Organization; value: string }) => {
    setIsEditing(true);
    setUpdatedOrganization((prev) => ({ ...prev, [field]: value }));
    setEditedFields((prev) => ({ ...prev, [field]: value }));
  };

  const handleAddressChange = ({ field, value }: { field: keyof Organization['address']; value: string }) => {
    setIsEditing(true);
    setUpdatedOrganization((prev) => ({
      ...prev,
      address: { ...prev.address, [field]: value },
    }));
    setEditedFields((prev) => ({
      ...prev,
      address: { ...prev.address, [field]: value },
    }));
  };

  const handleOfferChange = ({ field, value }: { field: keyof Organization['offer']; value: string | number | boolean }) => {
    setIsEditing(true);
    setUpdatedOrganization((prev) => ({
      ...prev,
      offer: { ...prev.offer, [field]: value },
    }));
    setEditedFields((prev) => ({
      ...prev,
      offer: { ...prev.offer, [field]: value },
    }));
  };

  return {
    isEditing,
    updatedOrganization,
    editedFields,
    handleIdentityChange,
    handleAddressChange,
    handleOfferChange,
    handleCancelEditing,
    handleSave,
  };
}
